import { Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { ExplanationAccordion, SortableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { ToDoListCancelledParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime, useDebounce } from 'utils'

export default function CancelledToDoListDetails() {
  const navigate = useNavigate()
  const layout = useLayout()

  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false
  })

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const [parameters, setParameters] = useState<ToDoListCancelledParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'createdDateTime',
    order: 'asc'
  })

  const getCanceledToDoList = useDebounce((parameters: ToDoListCancelledParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getCanceledToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Cancelled')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicles)
  }, [layout, loadingState.isGettingVehicles])

  useEffect(() => {
    getCanceledToDoList(parameters)
  }, [getCanceledToDoList, parameters])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
            <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
              <Grid item xs={12} sm='auto'>
                <TextField
                  fullWidth
                  label='Filter Field'
                  onChange={e => {
                    setFilterField(e.target.value)
                    setFilterValue('')
                  }}
                  select
                  sx={{ minWidth: 200 }}
                  value={filterField || ''}
                >
                  <MenuItem value='cancelledDateTime'>Canceled Date Time</MenuItem>

                  <MenuItem value='exporter'>Exporter</MenuItem>

                  <MenuItem value='vehicleType'>Vehicle Type</MenuItem>

                  <MenuItem value='vin'>VIN</MenuItem>

                  <MenuItem value='year'>Year</MenuItem>

                  <MenuItem value='make'>Make</MenuItem>

                  <MenuItem value='model'>Model</MenuItem>

                  <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>
                </TextField>
              </Grid>

              {['', 'exporter', 'vehicleType', 'vin', 'year', 'make', 'model', 'importerStockNumber'].includes(filterField) && (
                <Grid item xs={12} sm='auto'>
                  <TextField
                    disabled={!filterField}
                    label='Filter Value'
                    onChange={e => {
                      setFilterValue(e.target.value)
                    }}
                    value={filterValue || ''}
                  />
                </Grid>
              )}

              {filterField === 'cancelledDateTime' && (
                <>
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                      label='Filter Date From'
                      onChange={e => {
                        setFilterValueFrom(e.target.value)
                      }}
                      type='date'
                      value={filterValueFrom}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <TextField
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                      label='Filter Date To'
                      onChange={e => {
                        setFilterValueTo(e.target.value)
                      }}
                      type='date'
                      value={filterValueTo}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm='auto'>
                <Button
                  color='inherit'
                  disabled={
                    !filterField || (filterField === 'cancelledDateTime' ? !filterValueFrom || !filterValueTo : !filterValue)
                  }
                  variant='contained'
                  onClick={() => {
                    if (filterField === 'cancelledDateTime') {
                      setParameters({
                        ...parameters,
                        cancelledDateFrom: filterValueFrom,
                        cancelledDateTo: filterValueTo
                      })
                    } else {
                      setParameters({
                        ...parameters,
                        [filterField]: filterValue
                      })
                    }

                    setFilterField('')
                    setFilterValue('')
                    setFilterValueFrom('')
                    setFilterValueTo('')
                  }}
                >
                  APPLY FILTER
                </Button>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  color='secondary'
                  disabled={
                    !Boolean(
                      parameters.cancelledDateFrom ||
                        parameters.cancelledDateTo ||
                        parameters.exporter ||
                        parameters.vehicleType ||
                        parameters.vin ||
                        parameters.year ||
                        parameters.make ||
                        parameters.model ||
                        parameters.importerStockNumber
                    )
                  }
                  variant='text'
                  onClick={() => {
                    setParameters({
                      ...parameters,
                      cancelledDateFrom: undefined,
                      cancelledDateTo: undefined,
                      exporter: undefined,
                      vehicleType: undefined,
                      vin: undefined,
                      year: undefined,
                      make: undefined,
                      model: undefined,
                      importerStockNumber: undefined
                    })
                  }}
                >
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} sm='auto' container alignItems='center' justifyContent='flex-end'></Grid>
          </Grid>

          <Grid item xs={12} container spacing={1} alignItems='center'>
            {parameters.cancelledDateFrom && parameters.cancelledDateTo && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Created Date from "${moment(parameters.cancelledDateFrom).format(
                    'M/D/yyyy hh:mm:ss A'
                  )}" to "${moment(parameters.cancelledDateTo).format('M/D/yyyy hh:mm:ss A')}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      cancelledDateFrom: undefined,
                      cancelledDateTo: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.exporter && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Exporter contains "${parameters.exporter}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      exporter: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.vehicleType && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Vehicle Type contains "${parameters.vehicleType}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      vehicleType: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.vin && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`VIN contains "${parameters.vin}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      vin: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.year && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Year is ${parameters.year}`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      year: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.make && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Make contains "${parameters.make}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      make: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.model && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Model contains "${parameters.model}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      model: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}

            {parameters.importerStockNumber && (
              <Grid item xs={12} sm='auto'>
                <MuiChip
                  color='secondary'
                  label={`Importer Stock # contains "${parameters.importerStockNumber}"`}
                  onDelete={() => {
                    setParameters({
                      ...parameters,
                      importerStockNumber: undefined
                    })
                  }}
                  variant='outlined'
                />
              </Grid>
            )}
          </Grid>

          <Grid item xs={12}>
            <ListBody>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Table>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='cancelledDateTime'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Canceled Date Time
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='exporter'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Exporter
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='vehicleType'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Vehicle Type
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='vin'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            VIN
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='year'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Year
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='make'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Make
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='model'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Model
                          </SortableTableCell>

                          <SortableTableCell
                            onChangeSortByDirection={onChangeSortByDirection}
                            propertyName='importerStockNumber'
                            sortBy={parameters.orderBy}
                            sortDirection={parameters.order}
                          >
                            Importer Stock #
                          </SortableTableCell>
                          <TableCell padding='checkbox' size='small'></TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {vehicles?.map((vehicle, index) => (
                          <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                            <TableCell>{vehicle.cancelledDateTime ? formatDateTime(vehicle.cancelledDateTime) : ''}</TableCell>

                            <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                            <TableCell>{vehicle.vehicleType?.name}</TableCell>

                            <TableCell>{vehicle.vin}</TableCell>

                            <TableCell>{vehicle.year}</TableCell>

                            <TableCell>{vehicle.make}</TableCell>

                            <TableCell>{vehicle.model}</TableCell>

                            <TableCell>{vehicle.importerStockNumber}</TableCell>

                            <TableCell padding='checkbox' size='small'>
                              <Button
                                color='primary'
                                endIcon={<Edit />}
                                onClick={() => {
                                  navigate('/vehicles/' + vehicle.vehicleGuid)
                                }}
                                size='small'
                                variant='text'
                              >
                                EDIT
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>

                      <TableFooter>
                        <TableRow>
                          <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            SelectProps={{ fullWidth: false }}
                            count={totalCount}
                            rowsPerPage={parameters.pageSize}
                            page={parameters.page}
                            onPageChange={(_, page) => {
                              setParameters({
                                ...parameters,
                                page
                              })
                            }}
                            onRowsPerPageChange={e => {
                              setParameters({
                                ...parameters,
                                page: 0,
                                pageSize: e.target.value as unknown as number
                              })
                            }}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                </Table>
              </Box>
            </ListBody>
          </Grid>

          <Grid item xs={12}>
            <ExplanationAccordion variant='outlined'>
              || Vehicles appear in this to do list if “CANCEL IMPORT” was pressed for that vehicle. This is for vehicles that
              never come across the border. Vehicles cannot be deleted from VTS2. || The Canceled “To Do List” is a list screen
              intended for users on a PC. ||
            </ExplanationAccordion>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
